import { useEffect, useState } from 'react';
import {
  DEFAULT_COURSES,
  SKILLS,
  formatPence,
  type Course,
  type CourseProgressView,
  type QuizAnswerReply,
  type QuizView,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { actionKey, londonClock } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

type View =
  { kind: 'list' } | { kind: 'course'; id: string } | { kind: 'notes'; id: string } | { kind: 'quiz'; id: string };

// Courses, as Lagos Life's 🎓 Courses: the courses on offer, each one's lessons (attend the class, read the notes,
// pass the quiz), and the quiz itself, one question at a time
export function CoursesScreen(props: PhoneAppProps) {
  const content = useCatalogue()?.courses ?? DEFAULT_COURSES;
  const place = useCatalogue()?.place(content.placeId)?.name ?? 'the university';
  const { reply, setReply, failure, busy, load, run } = usePhoneApp(() => api.courses.mine(), props);
  const [view, setView] = useState<View>({ kind: 'list' });
  const courseOf = (id: string) => content.courses.find((course) => course.id === id);
  const progressOf = (id: string) => reply?.courses.find((course) => course.id === id);
  const open = view.kind === 'list' ? null : courseOf(view.id);
  const back =
    view.kind === 'list'
      ? undefined
      : view.kind === 'course'
        ? { label: '‹ Courses', onClick: () => setView({ kind: 'list' }) }
        : { label: '‹ Lessons', onClick: () => setView({ kind: 'course', id: view.id }) };
  return (
    <PhoneAppFrame
      id="courses"
      title={open ? `${open.emoji} ${open.label}` : '🎓 Courses'}
      className="courses-screen"
      closeLabel="Close Courses"
      back={back}
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening the prospectus…'}
    >
      {reply && view.kind === 'list' && (
        <div className="business-detail">
          <p className="business-muted">
            Enrol once, then attend each lesson&apos;s class at {place} ({content.classesPerDay} a day), read the notes
            and pass the quiz. Classes today: {reply.classesToday} of {content.classesPerDay}.
          </p>
          <ul className="courses-list" aria-label="Courses on offer">
            {reply.courses.map((progress) => {
              const course = courseOf(progress.id);
              if (!course) return null;
              return (
                <li key={course.id}>
                  <button type="button" onClick={() => setView({ kind: 'course', id: course.id })}>
                    <span aria-hidden="true">{progress.certified ? '📜' : course.emoji}</span>
                    <span>
                      <strong>{course.label}</strong>
                      <small>{statusOf(course, progress)}</small>
                    </span>
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {reply && open && view.kind === 'course' && (
        <CourseView
          course={open}
          progress={progressOf(open.id)!}
          atClass={reply.atClass}
          classesLeft={Math.max(0, content.classesPerDay - reply.classesToday)}
          perDay={content.classesPerDay}
          wallet={reply.walletPence}
          place={place}
          busy={busy}
          onEnrol={() => void run(() => api.courses.enrol(open.id, actionKey('course')))}
          onAttend={() =>
            reply.atClass ? props.onDo?.(content.classActivityId, open.id) : props.onPlace?.(content.placeId)
          }
          onNotes={() => setView({ kind: 'notes', id: open.id })}
        />
      )}
      {reply && open && view.kind === 'notes' && (
        <Notes course={open} progress={progressOf(open.id)!} onQuiz={() => setView({ kind: 'quiz', id: open.id })} />
      )}
      {reply && open && view.kind === 'quiz' && (
        <Quiz
          course={open}
          onDone={(next) => {
            setReply(next.courses);
            if (next.courses.message) notify(next.courses.message);
            props.onWallet();
            setView({ kind: 'course', id: open.id });
          }}
          onBack={() => setView({ kind: 'notes', id: open.id })}
        />
      )}
    </PhoneAppFrame>
  );
}

function statusOf(course: Course, progress: CourseProgressView) {
  if (progress.certified) return 'Certificate earned';
  if (!progress.enrolled)
    return `${formatPence(course.feePence)} · ${course.lessons.length} lessons · ${SKILLS[course.skill].label}`;
  if (progress.notesFrom) return `Lesson ${progress.done + 1}: notes ready, quiz to pass`;
  return `Lesson ${progress.done + 1} of ${progress.lessons}: attend the class`;
}

function CourseView({
  course,
  progress,
  atClass,
  classesLeft,
  perDay,
  wallet,
  place,
  busy,
  onEnrol,
  onAttend,
  onNotes,
}: {
  course: Course;
  progress: CourseProgressView;
  atClass: boolean;
  classesLeft: number;
  perDay: number;
  wallet: number;
  place: string;
  busy: boolean;
  onEnrol: () => void;
  onAttend: () => void;
  onNotes: () => void;
}) {
  const notesOut = progress.notesReady;
  return (
    <div className="business-detail">
      <p className="business-muted">{course.blurb}</p>
      <p className="business-muted">
        {SKILLS[course.skill].icon} {SKILLS[course.skill].label}
        {course.career ? ' · a certificate gets you promoted sooner in its job' : ' · a life skill'}
      </p>
      <ol className="courses-lessons" aria-label="Lessons">
        {course.lessons.map((lesson, index) => {
          const passed = index < progress.done;
          const current = index === progress.done && progress.enrolled && !progress.certified;
          return (
            <li key={lesson.title} className={passed ? 'passed' : current ? 'current' : ''}>
              <span aria-hidden="true">{passed ? '✅' : current ? '📖' : '🔒'}</span>
              <span>
                <strong>
                  {index + 1}. {lesson.title}
                </strong>
                <small>
                  {passed
                    ? 'Passed'
                    : current
                      ? progress.notesFrom
                        ? notesOut
                          ? 'Notes ready · read them, then the quiz'
                          : `In class · notes at ${londonClock(progress.notesFrom)}`
                        : 'Next: attend the class'
                      : 'After the lesson before'}
                </small>
              </span>
            </li>
          );
        })}
      </ol>
      {!progress.enrolled && (
        <button type="button" className="city-card-go" disabled={busy || wallet < course.feePence} onClick={onEnrol}>
          {wallet < course.feePence
            ? `Need ${formatPence(course.feePence)}`
            : `Enrol · ${formatPence(course.feePence)}`}
        </button>
      )}
      {progress.enrolled && !progress.certified && !progress.notesFrom && (
        <button type="button" className="city-card-go" disabled={busy || classesLeft === 0} onClick={onAttend}>
          {classesLeft === 0
            ? `${perDay} classes today: come back tomorrow`
            : atClass
              ? `Attend lesson ${progress.done + 1}`
              : `Go to ${place} for lesson ${progress.done + 1}`}
        </button>
      )}
      {progress.enrolled && progress.notesFrom && (
        <button type="button" className="city-card-go" disabled={!notesOut} onClick={onNotes}>
          {notesOut ? `📖 Read the notes for lesson ${progress.done + 1}` : 'The notes come after the class'}
        </button>
      )}
      {progress.certified && <p className="courses-certificate">📜 Certificate earned: {course.label}</p>}
    </div>
  );
}

function Notes({ course, progress, onQuiz }: { course: Course; progress: CourseProgressView; onQuiz: () => void }) {
  const lesson = course.lessons[progress.done];
  if (!lesson) return null;
  const waiting = progress.retryMinutes;
  return (
    <div className="business-detail">
      <article className="courses-notes" aria-label={`Notes: ${lesson.title}`}>
        <h3>
          Lesson {progress.done + 1}: {lesson.title}
        </h3>
        {lesson.notes.map((note) => (
          <p key={note}>{note}</p>
        ))}
      </article>
      <button type="button" className="city-card-go" disabled={waiting > 0} onClick={onQuiz}>
        {waiting > 0 ? `The quiz reopens in ${waiting} min` : 'Take the quiz'}
      </button>
    </div>
  );
}

function Quiz({
  course,
  onDone,
  onBack,
}: {
  course: Course;
  onDone: (reply: QuizAnswerReply) => void;
  onBack: () => void;
}) {
  const [quiz, setQuiz] = useState<QuizView | null>(null);
  const [marked, setMarked] = useState<QuizAnswerReply | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.courses
      .quiz(course.id)
      .then((next) => alive && setQuiz(next))
      .catch((error: unknown) => {
        if (!alive) return;
        notify(describeError(error).message);
        onBack();
      });
    return () => {
      alive = false;
    };
    // one sitting per opening of the quiz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);
  if (!quiz) return <p className="business-muted">Loading the quiz…</p>;
  const question = quiz.questions[marked ? marked.quiz.at - 1 : quiz.at];
  if (!question) return null;
  const answer = async (text: string) => {
    if (marked || busy) return;
    setBusy(true);
    setChosen(text);
    try {
      setMarked(await api.courses.answer(course.id, text));
    } catch (error) {
      notify(describeError(error).message);
      setChosen(null);
    } finally {
      setBusy(false);
    }
  };
  const next = () => {
    if (!marked) return;
    if (marked.finished) return onDone(marked);
    setQuiz(marked.quiz);
    setMarked(null);
    setChosen(null);
  };
  const score = marked?.quiz ?? quiz;
  return (
    <div className="business-detail">
      <p className="business-muted" aria-live="polite">
        Question {(marked ? marked.quiz.at : quiz.at + 1).toString()} of {quiz.questions.length} · {score.right} right ·{' '}
        {quiz.pass} to pass
      </p>
      <section className="courses-question" aria-label="Question">
        <strong>{question.q}</strong>
        <div className="courses-answers">
          {question.answers.map((text) => {
            const state = !marked ? '' : text === marked.rightAnswer ? 'right' : text === chosen ? 'wrong' : '';
            return (
              <button
                type="button"
                key={text}
                className={state}
                disabled={busy || Boolean(marked)}
                aria-pressed={chosen === text}
                onClick={() => void answer(text)}
              >
                {text}
              </button>
            );
          })}
        </div>
      </section>
      {marked && (
        <p className={marked.correct ? 'courses-verdict right' : 'courses-verdict wrong'} role="status">
          {marked.correct ? '✅ Right!' : `❌ Not quite. It's: ${marked.rightAnswer}`}
        </p>
      )}
      {marked && (
        <button type="button" className="city-card-go" onClick={next}>
          {marked.finished ? (marked.passed ? '🎉 Lesson passed: carry on' : 'See your notes again') : 'Next question'}
        </button>
      )}
    </div>
  );
}
